import React, { useCallback, useEffect, useRef } from 'react';
import type { HomeTopicSectionConfig } from '../homeTopicSections';
import type { HomeCardLayout } from '../constants/homeCardLayouts';
import { QuickServiceTileSection } from './QuickServiceTileSection';
import { TopicServiceCard } from './TopicServiceCard';
import { resolveHomeItemTileIcon } from '../utils/tablerIcon';

function quickSectionVariant(
  layout: HomeCardLayout,
  sectionId: string,
): {
  variant?: string;
  labelInside?: boolean;
} {
  switch (layout) {
    case 'quick_emergency':
      return { variant: 'towing', labelInside: true };
    case 'quick_professional':
      return { variant: 'professional', labelInside: true };
    case 'quick_icons':
      return {
        variant: sectionId === 'financial' ? 'financial' : undefined,
        labelInside: false,
      };
    default:
      return {};
  }
}

export interface CmsHomeQuickSectionProps {
  section: HomeTopicSectionConfig;
  layout: HomeCardLayout;
  onSelect: (categoryId: string, slug: string) => void;
  hideTitle?: boolean;
}

export const CmsHomeQuickSection: React.FC<CmsHomeQuickSectionProps> = ({
  section,
  layout,
  onSelect,
  hideTitle = false,
}) => {
  if (layout === 'vehicle_carousel') {
    return <CmsVehicleCarouselSection section={section} onSelect={onSelect} />;
  }

  const { variant, labelInside } = quickSectionVariant(layout, section.id);
  const title = section.titleLead
    ? `${section.titleLead}${section.titleTail ? ` ${section.titleTail}` : ''}`
    : section.title || section.eyebrow;

  const items = section.items.map((item) => ({
    categoryId: item.categoryId,
    slug: item.slug,
    title: item.title,
    badge: item.badge,
    badgeType: item.badgeType,
    icon: resolveHomeItemTileIcon(item.iconKey, item.slug, item.categoryId),
  }));

  return (
    <QuickServiceTileSection
      id={section.id}
      title={hideTitle ? null : title}
      items={items}
      onSelect={onSelect}
      variant={variant}
      labelInside={labelInside}
    />
  );
};

const CmsVehicleCarouselSection: React.FC<{
  section: HomeTopicSectionConfig;
  onSelect: (categoryId: string, slug: string) => void;
}> = ({ section, onSelect }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const updateCardScales = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const focusX = scrollerRect.left + scrollerRect.width * 0.42;
    const cards = scroller.querySelectorAll<HTMLElement>('.vehicle-market-carousel__card');

    cards.forEach((card) => {
      const rect = card.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const delta = (center - focusX) / Math.max(scrollerRect.width, 1);
      const distance = Math.min(1, Math.abs(delta));
      const fromRight = delta > 0;
      const shrink = fromRight ? 0.12 : 0.06;
      const scale = 1 - distance * shrink;

      card.style.transform = `scale(${scale})`;
      card.style.opacity = String(1 - distance * 0.08);
    });
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        updateCardScales();
      });
    };

    updateCardScales();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [section.items.length, updateCardScales]);

  if (section.items.length === 0) return null;

  const title = section.title || section.eyebrow;

  return (
    <section
      id={`home-section-${section.id}`}
      className="legal-quick-section legal-quick-section--vehicle"
      aria-labelledby={`quick-section-title-${section.id}`}
    >
      <h2 id={`quick-section-title-${section.id}`} className="legal-quick-section__title">
        {title}
      </h2>
      <div ref={scrollerRef} className="catalog-grid vehicle-market-carousel">
        {section.items.map((item, index) => (
          <div key={`${item.categoryId}-${item.slug}`} className="catalog-grid-item">
            <div className="vehicle-market-carousel__card">
              <TopicServiceCard item={item} index={index} onSelect={onSelect} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
